import { logger } from '@automata/utils';
import range from 'lodash/range';
import React, { ComponentProps, FC, useCallback, useMemo } from 'react';

import { FormSectionLabel } from './FormSectionLabel';
import { updateLabwareSlotForTask } from '../../../features/graph/graphSlice';
import { selectLabwareSlotMappingByTaskId } from '../../../features/graph/selectors';
import { selectLabwareDisplayDataByTaskId } from '../../../features/selectors';
import { selectLabwareIdsForTaskByTaskId } from '../../../features/selectors/selectLabwareIdsForTaskByTaskId';
import { selectTaskPropertiesStrict } from '../../../features/workflow/selectors';
import { useAppDispatch, useAppSelector } from '../../../hooks';
import type { DeckLayoutData } from '../../../types';
import {
  DeckLayout,
  type HandleChangeSlotFn,
  type LabwareSlotData,
} from '../../DeckLayout';

export const DeckLayoutSection: FC<ComponentProps<typeof DeckLayout>> = (
  props
) => {
  return (
    <>
      <FormSectionLabel>Labware & Deck</FormSectionLabel>

      <DeckLayout {...props} />
    </>
  );
};

export const ConnectedDeckLayoutSection: FC<{ taskId: string }> = ({
  taskId,
}) => {
  const dispatch = useAppDispatch();
  const taskProperties = useAppSelector(selectTaskPropertiesStrict)[taskId];
  const slotMapping = useAppSelector(selectLabwareSlotMappingByTaskId)[taskId];
  const labwareDisplayData = useAppSelector(selectLabwareDisplayDataByTaskId)[
    taskId
  ];
  const labwareByHandle = useAppSelector(selectLabwareIdsForTaskByTaskId)[
    taskId
  ];

  // TODO(IL, 2024-04-01): the deck should depend on the instrument, which needs deck layout data!
  const HARDCODED_DECK_LAYOUT_DATA: DeckLayoutData = {
    deckLayoutType: 'grid',
    rows: 3,
    columns: 3,
  };

  // TODO(IL, 2024-04-05): this should come from instrument data
  const instrumentSlots: number[] = range(
    HARDCODED_DECK_LAYOUT_DATA.rows * HARDCODED_DECK_LAYOUT_DATA.columns
  ).map((i) => i + 1);

  const handleChangeLoadOrder = () => {
    logger.warn('NOT IMPLEMENTED: change load order');
  };
  const handleChangeUnloadOrder = () => {
    logger.warn('NOT IMPLEMENTED: change unload order');
  };

  const handleChangeSlot = useCallback<HandleChangeSlotFn>(
    (labwareId, slot) => {
      dispatch(updateLabwareSlotForTask({ taskId, labwareId, slot }));
    },
    [dispatch, taskId]
  );

  // TODO IMMEDIATELY this should be a selector, and LabwareLayoutData shouldn't diverge unnecessarily from LabwareDataForTask etc
  //
  // TODO(IL, 2024-04-23): this "labwareSlotData" is a temporary fix to deal with
  // overlapping slots. If overlapping slots is unavoidable, present the problem to
  // the user and remove labwareDataBySlot prop in favor of labwareSlotData.
  // If overlapping slots is made impossible, remove labwareSlotData and just use labwareDataBySlot.
  const labwareSlotData = useMemo<LabwareSlotData>(() => {
    if (labwareByHandle === undefined) {
      return [];
    }

    const mappedSlots = Object.values(slotMapping ?? {});
    const unmappedSlots = instrumentSlots.filter(
      (s) => !mappedSlots.includes(s)
    );

    return Object.entries(labwareByHandle).flatMap(
      ([handlePairId, labwareId]) => {
        if (labwareId === null) {
          return [];
        }

        const displayData = labwareDisplayData?.[handlePairId];
        if (displayData == null) {
          return [];
        }
        const { colorIndex, labwareName } = displayData;

        let slot = slotMapping?.[labwareId];

        if (slot === undefined) {
          // TODO(IL, 2024-04-05): by default, we'll have labware land in highest unmapped slot.
          const nextSlot = unmappedSlots.pop();
          if (nextSlot !== undefined) {
            slot = nextSlot;
          } else {
            logger.warn(
              `No slots remaining, assigning ${labwareId} to slot 1 as a fallback...`
            );
            slot = 1;
          }
        }

        return {
          slot,
          data: {
            id: labwareId,
            labwareName,
            colorIndex,
            slot,
            loadOrder: 0,
            unloadOrder: 0,
          },
        };
      },
      {}
    );
  }, [instrumentSlots, labwareByHandle, labwareDisplayData, slotMapping]);

  if (taskProperties === undefined) {
    return null;
  }

  const { taskType } = taskProperties;

  return taskType === 'runLiquidHandler' ? (
    <DeckLayoutSection
      handleChangeLoadOrder={handleChangeLoadOrder}
      handleChangeUnloadOrder={handleChangeUnloadOrder}
      handleChangeSlot={handleChangeSlot}
      // TODO IMMEDIATELY load order not implemented
      loadOrder="lastToFirst"
      unloadOrder="firstToLast"
      deckLayoutData={HARDCODED_DECK_LAYOUT_DATA}
      labwareSlotData={labwareSlotData}
    />
  ) : null;
};
